import React, { useEffect, useState } from 'react';
import {
  Snackbar, Card, CardContent, Typography, Button, IconButton, Box, Stack,
} from '@mui/material';
import { Close, GetApp, IosShare, AddBox } from '@mui/icons-material';

// Local-storage key for the "dismiss for 7 days" cooldown.
const DISMISS_KEY = 'the_church_install_dismissed_until';
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true; // iOS Safari

const isIos = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
  !/crios|fxios/i.test(window.navigator.userAgent); // native Safari only

const dismissed = () => {
  const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return until > Date.now();
};

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null); // beforeinstallprompt event
  const [showIosHint, setShowIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || dismissed()) return;

    // Chrome / Edge / Android
    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBIP);

    // Detect successful install to hide our UI immediately.
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      localStorage.removeItem(DISMISS_KEY);
    };
    window.addEventListener('appinstalled', onInstalled);

    // iOS Safari — no beforeinstallprompt event, so surface a manual hint
    // after a short delay so it doesn't interrupt the first paint.
    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => {
        setShowIosHint(true);
        setVisible(true);
      }, 4000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      const choice = await deferred.userChoice;
      if (choice?.outcome === 'accepted') {
        setVisible(false);
      } else {
        // User dismissed the native prompt — respect their choice for a week.
        localStorage.setItem(DISMISS_KEY, String(Date.now() + COOLDOWN_MS));
        setVisible(false);
      }
    } finally {
      setDeferred(null);
    }
  };

  const handleClose = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + COOLDOWN_MS));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{
        bottom: { xs: 16, sm: 24 },
        left: { xs: 12, sm: 24 },
        right: { xs: 12, sm: 24 },
        transform: 'none',
      }}
    >
      <Card
        sx={{
          width: { xs: '100%', sm: 460 },
          maxWidth: '100%',
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid #E2E8F0',
          boxShadow: '0 20px 40px rgba(15,23,42,0.18)',
        }}
      >
        <Box sx={{
          background: 'linear-gradient(135deg, #0B2545 0%, #13315C 100%)',
          color: '#fff',
          px: 2.5, py: 1.75,
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Box
            component="img" src="/icon.svg" alt="The Church"
            sx={{ width: 44, height: 44, borderRadius: 1.5, bgcolor: '#fff2' }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={800} lineHeight={1.1}>
              Install The Church
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Faster access · Full-screen · Works from your home screen
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} sx={{ color: '#fff' }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <CardContent sx={{ px: 2.5, py: 2 }}>
          {showIosHint ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                To install on iPhone or iPad, tap the <b>Share</b> button in
                Safari, then choose <b>Add to Home Screen</b>.
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                <IosShare sx={{ color: '#0B2545' }} />
                <Typography variant="caption" color="text.secondary">1. Tap Share</Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <AddBox sx={{ color: '#0B2545' }} />
                <Typography variant="caption" color="text.secondary">2. Tap "Add to Home Screen"</Typography>
              </Stack>
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                <Button size="small" variant="text" onClick={handleClose}>Got it</Button>
              </Stack>
            </Box>
          ) : (
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" variant="text" onClick={handleClose}>
                Not now
              </Button>
              <Button
                size="small" variant="contained" startIcon={<GetApp />}
                onClick={handleInstall}
                sx={{ bgcolor: '#0B2545', '&:hover': { bgcolor: '#13315C' } }}
              >
                Install
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Snackbar>
  );
}
